/**
 * Where a health reading came from.
 *
 * `services/health/store.js` has persisted a per-reading `source` field since it
 * was written — and the sync has never populated it, so the column exists and
 * is always empty. That matters more than it looks, because the wellness
 * escalation gate now treats a recent heart-rate sample as evidence that a
 * living person is wearing a watch (quiet-baseline.js).
 *
 * That inference is sound but INDIRECT: it rests on "iPhones do not measure
 * heart rate". Two things break it, and both are invisible without provenance:
 *
 *   - a hand-typed reading. A caregiver entering "72 bpm" from a home monitor
 *     is a record of the past, not proof of a pulse now. Counting it as
 *     liveness would suppress an escalation on the strength of somebody's
 *     typing.
 *   - a third-party app back-filling history. Same shape, same problem.
 *
 * So the classification is deliberately conservative: anything we cannot
 * positively identify as a wearable is NOT a wearable. The cost of a false
 * 'watch' is a suppressed alert on a person who may be on the floor; the cost
 * of a false 'unknown' is an alert that fires as it does today.
 *
 * Normalised on the client, where the platform metadata actually lives, so the
 * server stores one small vocabulary instead of re-parsing two vendors' shapes.
 */

export type HealthSourceKind = 'watch' | 'phone' | 'app' | 'manual' | 'unknown';

export type HealthSource = {
  /** Display name, e.g. "Zach's Apple Watch". Null when the platform withholds it. */
  name: string | null;
  kind: HealthSourceKind;
  /** Hand-entered by a human. Never counts as liveness. */
  manual: boolean;
};

/** Apple productType / device model strings that denote a worn device. */
const APPLE_WEARABLE = /^watch/i;
const APPLE_PHONE = /^iphone/i;

/**
 * Classify an iOS HealthKit sample.
 *
 * `metadataWasUserEntered` is HealthKit's own flag for hand entry and is
 * checked FIRST — a manually typed reading on an Apple Watch is still manual.
 */
export function classifyHealthKitSource(sample: any): HealthSource {
  const device = sample?.device || {};
  const revision = sample?.sourceRevision || {};
  const name = device.name
    || sample?.metadataDeviceName
    || revision?.source?.name
    || null;

  if (sample?.metadataWasUserEntered === true) {
    return { name, kind: 'manual', manual: true };
  }

  // productType is the hardware identifier ("Watch6,1", "iPhone14,2") and is
  // the most reliable of the three; device.model is a friendlier "Watch".
  const hardware = String(revision.productType || device.model || '');
  if (APPLE_WEARABLE.test(hardware)) return { name, kind: 'watch', manual: false };
  if (APPLE_PHONE.test(hardware)) return { name, kind: 'phone', manual: false };

  // A sample with no device at all came from an app writing into HealthKit
  // rather than from hardware we can vouch for.
  if (!device.model && !revision.productType) {
    return { name, kind: revision?.source?.name ? 'app' : 'unknown', manual: false };
  }
  return { name, kind: 'unknown', manual: false };
}

/** Health Connect DeviceType values that denote something worn on the body. */
const HC_WEARABLE_TYPES = new Set([4 /* RING */, 6 /* FITNESS_BAND */, 7 /* CHEST_STRAP */]);
const HC_PHONE_TYPE = 2;
const HC_MANUAL_ENTRY = 3;

/**
 * Classify an Android Health Connect record.
 *
 * Health Connect has no WATCH device type — a Wear OS watch reports as
 * FITNESS_BAND or, frequently, UNKNOWN with only a manufacturer. Rather than
 * guess a watch from a manufacturer string, anything unidentifiable stays
 * 'unknown' and simply does not count as liveness.
 */
export function classifyHealthConnectSource(record: any): HealthSource {
  const meta = record?.metadata || {};
  const device = meta.device || {};
  const name = [device.manufacturer, device.model].filter(Boolean).join(' ').trim()
    || meta.dataOrigin
    || null;

  if (meta.recordingMethod === HC_MANUAL_ENTRY) {
    return { name, kind: 'manual', manual: true };
  }
  if (HC_WEARABLE_TYPES.has(Number(device.type))) return { name, kind: 'watch', manual: false };
  if (Number(device.type) === HC_PHONE_TYPE) return { name, kind: 'phone', manual: false };
  if (!device.type && meta.dataOrigin) return { name, kind: 'app', manual: false };
  return { name, kind: 'unknown', manual: false };
}

/**
 * Does this reading count as evidence that a living person is wearing a device
 * right now?
 *
 * Only a wearable, and only when not hand-entered. Exported so the client and
 * the server-side gate agree on one definition rather than each having an
 * opinion.
 */
export function isWearableLiveness(source?: HealthSource | null): boolean {
  return !!source && source.kind === 'watch' && !source.manual;
}
